import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import {
  DetailList,
  DetailListSkeleton,
  type DetailGroup,
} from "@/components/DetailList";
import { EnrollmentChecklist } from "@/components/EnrollmentChecklist";
import { OnlineStatusBadge } from "@/components/OnlineStatusBadge";
import { useApi } from "@/lib/api";
import { formatDate } from "@/lib/formatDate";
import { POLL_INTERVAL_MS } from "@/lib/onlineStatus";
import { HeartbeatValue } from "./HeartbeatValue";
import { SetupDisclosure } from "./SetupDisclosure";

interface ConnectorDetailsData {
  id: string;
  name: string;
  createdAt: string;
  lastSeenAt: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  platform: string | null;
}

export function ConnectorDetails() {
  const { accessToken } = useAuth();
  const api = useApi();
  const { orgId, connectorId } = useParams();
  const [connector, setConnector] = useState<ConnectorDetailsData | null>(null);

  const loadConnector = useCallback(() => {
    if (!accessToken || !orgId || !connectorId) {
      return;
    }

    void api
      .get<ConnectorDetailsData>(
        `/organizations/${orgId}/connectors/${connectorId}`,
      )
      .then(setConnector);
  }, [accessToken, api, orgId, connectorId]);

  useEffect(loadConnector, [loadConnector]);

  useEffect(() => {
    const interval = setInterval(loadConnector, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [loadConnector]);

  if (!connector) {
    return <DetailListSkeleton rows={6} />;
  }

  const hasNeverConnected = !connector.lastSeenAt;

  const groups: DetailGroup[] = [
    {
      title: "Connection",
      rows: [
        {
          label: "Status",
          value: <OnlineStatusBadge lastSeenAt={connector.lastSeenAt} />,
        },
        {
          label: "Last heartbeat",
          value: <HeartbeatValue lastSeenAt={connector.lastSeenAt} />,
        },
        {
          label: "IP address",
          value: connector.ipAddress ?? "Unknown",
        },
      ],
    },
    {
      title: "Host",
      rows: [
        {
          label: "Client version",
          value: connector.userAgent ?? "Unknown",
        },
        {
          label: "Platform",
          value: connector.platform ?? "Unknown",
        },
        { label: "Created", value: formatDate(connector.createdAt) },
      ],
    },
  ];

  return (
    <div className="space-y-5">
      {hasNeverConnected && (
        <EnrollmentChecklist
          entityType="connector"
          createdAt={connector.createdAt}
          lastSeenAt={connector.lastSeenAt}
        />
      )}

      <DetailList groups={groups} />

      {!hasNeverConnected && (
        <SetupDisclosure
          entityType="connector"
          createdAt={connector.createdAt}
          lastSeenAt={connector.lastSeenAt}
        />
      )}
    </div>
  );
}
